import { DataProxy } from '@apollo/client'

import { CachedListUpdateStrategy, updateCachedItem, updateCachedList } from '@acre/utils'
import {
  AddCaseMutation,
  AddClientAndUpdateCaseMutation,
  AddPropertyMutation,
  ArrayElement,
  CaseStatus,
  DeleteCasePropertyMutation,
  GetAllCasesDocument,
  GetCaseClientsDocument,
  GetCaseClientsQuery,
  GetCaseDocument,
  GetCaseListDocument,
  GetCasePropertiesDocument,
  GetClientCaseListDocument,
  UpdateCaseMutation,
  UpdatePropertyMutation,
} from '@acre/graphql'

import { CasePropertyType } from '../../pages/Properties/Property.helpers'
import { propertyTypes } from './property'

export const updateCaseCachePatch = async (cache: DataProxy, data: UpdateCaseMutation, caseId: string) => {
  const { updateCase } = data
  if (updateCase) {
    await updateCachedItem<UpdateCaseMutation['updateCase']>(cache, {
      query: GetCaseDocument,
      variables: { id: caseId },
      data: updateCase,
      key: 'case',
    })
  }
}

// TODO: FRON-1099 figure out property types and if needed include pref change cache update
export const updateCasePropertiesPost = async (
  cache: DataProxy,
  data: AddPropertyMutation['addProperty'],
  caseId: string,
  propertyType: CasePropertyType,
  reasons?: string[],
) => {
  if (data) {
    await updateCachedItem<AddPropertyMutation['addProperty']>(cache, {
      query: GetCasePropertiesDocument,
      variables: { id: caseId, includeCaseDetailsInProperty: true },
      data,
      key: `case.details.${propertyTypes[propertyType]}`,
    })
  }
  if (reasons) {
    await updateCachedItem<string[]>(cache, {
      query: GetCasePropertiesDocument,
      variables: { id: caseId, includeCaseDetailsInProperty: true },
      data: reasons,
      key: `case.details.change_of_preference_reason`,
    })
  }
}

export const deleteCasePropertiesPatch = async (
  cache: DataProxy,
  caseId: string,
  propertyType: CasePropertyType,
  reasons: string[],
) => {
  await updateCachedItem<DeleteCasePropertyMutation['deleteCaseProperty']>(cache, {
    query: GetCasePropertiesDocument,
    variables: { id: caseId },
    data: null,
    key: `case.details.${propertyTypes[propertyType]}`,
  })
  if (reasons) {
    await updateCachedItem<string[]>(cache, {
      query: GetCasePropertiesDocument,
      variables: { id: caseId },
      data: reasons,
      key: `case.details.change_of_preference_reason`,
    })
  }
}

export const updatePropertyPatch = async (
  cache: DataProxy,
  data: UpdatePropertyMutation,
  caseId: string,
  propertyType: CasePropertyType,
) => {
  const { updateProperty } = data

  if (updateProperty) {
    await updateCachedItem<UpdatePropertyMutation['updateProperty']>(cache, {
      query: GetCasePropertiesDocument,
      variables: { id: caseId, includeCaseDetailsInProperty: true },
      data: updateProperty,
      key: `case.details.${propertyTypes[propertyType]}`,
    })
  }
}

export const updateCaseClientList = (cache: DataProxy, caseId: string, data: AddClientAndUpdateCaseMutation) => {
  const { addClientAndUpdateCase } = data

  if (addClientAndUpdateCase && addClientAndUpdateCase.details) {
    updateCachedList<ArrayElement<GetCaseClientsQuery['case']['details']['clients']>>(cache, {
      readQuery: { query: GetCaseClientsDocument, variables: { id: caseId } },
      writeQuery: {
        query: GetCaseClientsDocument,
        variables: { id: caseId },
      },
      newItem: addClientAndUpdateCase,
      strategy: CachedListUpdateStrategy.Append,
      key: 'case.details.clients',
    })
  }
}

export const updateCaseList = (
  cache: DataProxy,
  clientIds: string[],
  userId: string,
  organisationId: string,
  data?: AddCaseMutation,
) => {
  if (data && data.addCase) {
    // Update cache for listing all cases
    updateCachedList<AddCaseMutation['addCase']>(cache, {
      readQuery: { query: GetCaseListDocument, variables: { organisationId } },
      writeQuery: { query: GetCaseListDocument, variables: { organisationId } },
      newItem: data.addCase,
      strategy: CachedListUpdateStrategy.Prepend,
      key: 'casesResponse.cases',
    })

    try {
      // Try and read the dashboard cache. If it exists, then update it. If not, we don't
      // want to create a new cache, and will rely instead on the initial network reqest
      const readQuery = {
        query: GetAllCasesDocument,
        variables: {
          userId,
          status: CaseStatus.PreRecommendation,
        },
      }
      cache.readQuery(readQuery)
      // Update cache for the dashboard
      updateCachedList<AddCaseMutation['addCase']>(cache, {
        readQuery,
        writeQuery: {
          query: GetAllCasesDocument,
          variables: {
            userId,
            status: CaseStatus.PreRecommendation,
          },
        },
        newItem: data.addCase,
        strategy: CachedListUpdateStrategy.Prepend,
        key: 'allCases.cases',
      })
    } catch (err) {
      // There is no cached data
      // This is currently the only way to check if a cache exists or not
      // https://github.com/apollographql/apollo-client/issues/1542
    }

    // Update cache for listing a client's cases
    clientIds.forEach((clientId) => {
      updateCachedList<AddCaseMutation['addCase']>(cache, {
        readQuery: {
          query: GetClientCaseListDocument,
          variables: { clientId },
        },
        writeQuery: {
          query: GetClientCaseListDocument,
          variables: { clientId },
        },
        newItem: data.addCase,
        strategy: CachedListUpdateStrategy.Prepend,
        key: 'clientCases',
      })
    })
  }
}
